import { useEffect, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";

/**
 * Session-wide uid → avatar-URL cache.
 *
 * Avatars are rendered in ~20 places (sidebar rows, message rows, the
 * header facepile, call cards, member lists, receipts, the mention menu…)
 * and most of those only have a uid and a display name to work with — not
 * the full CometChat.User object that actually carries getAvatar(). This
 * cache bridges that gap once, so every one of those places can show a
 * real photo without each having to fetch or thread a User object through.
 *
 * Entries are: a string (their photo), null (looked up, definitively has
 * none), or absent (not looked up yet).
 */

const cache = new Map<string, string | null>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

export function subscribeAvatars(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Records an avatar we already know, without a round trip. Called
 * wherever a real User object passes through (message senders, group
 * members, conversations) so the common cases never need a fetch. */
export function seedAvatarFromUser(user: { getUid?: () => string; getAvatar?: () => string } | null | undefined): void {
  const uid = user?.getUid?.();
  if (!uid) return;
  const url = user?.getAvatar?.() || null;
  if (cache.get(uid) === url) return; // no change — don't re-render everything
  cache.set(uid, url);
  emit();
}

/** Directly sets a uid's avatar — used right after an upload so the new
 * photo appears everywhere immediately, rather than only after a reload. */
export function setAvatarUrl(uid: string, url: string | null): void {
  if (cache.get(uid) === url) return;
  cache.set(uid, url);
  emit();
}

/** Looks a uid up, fetching once if we've never seen it. Deduped, and
 * failures cache as null so a missing/erroring user isn't retried on
 * every single render. */
function ensureAvatar(uid: string): void {
  if (!uid || cache.has(uid) || inFlight.has(uid)) return;
  inFlight.add(uid);
  CometChat.getUser(uid)
    .then((u) => {
      cache.set(uid, u?.getAvatar?.() || null);
    })
    .catch(() => {
      cache.set(uid, null);
    })
    .finally(() => {
      inFlight.delete(uid);
      emit();
    });
}

/** Subscribes to this uid's avatar, triggering a lookup if needed. */
export function useAvatarUrl(uid: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => (uid ? cache.get(uid) ?? null : null));

  useEffect(() => {
    if (!uid) {
      setUrl(null);
      return;
    }
    setUrl(cache.get(uid) ?? null);
    ensureAvatar(uid);
    return subscribeAvatars(() => setUrl(cache.get(uid) ?? null));
  }, [uid]);

  return url;
}
